// @ts-check
import { Edit2, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useRegisterUnsavedChanges } from '../context/UnsavedChangesContext';
import adminFashionModelService from '../services/adminFashionModelService';
import { numText } from '../utils/fashionModel';
import ImageUploadPreview from './ImageUploadPreview';

const EMPTY_FORM = {
  name: '',
  height: '',
  bust_in: '',
  waist_in: '',
  hips_in: '',
  shoe_size: '',
  usual_size: '',
  photo_url: '',
  is_active: true,
};

const inputClass = 'w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground';

/**
 * The fashion models KAAY shoots with. Picking one on a product fills its model measurements.
 * Models are deactivated, never deleted: products keep a soft reference to whoever was shot.
 */
const FashionModelsTab = ({ models, sizes, onUpdate, toast }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);
  const [formData, setFormDataRaw] = useState(EMPTY_FORM);

  const setField = useCallback((name, value) => {
    setIsDirty(true);
    setFormDataRaw((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const resetForm = () => {
    setFormDataRaw(EMPTY_FORM);
    setEditing(null);
    setShowForm(false);
    setIsDirty(false);
  };

  const handleCancel = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    resetForm();
  };

  const handleEdit = (model) => {
    setFormDataRaw({
      name: model.name,
      height: model.height,
      bust_in: numText(model.bust_in),
      waist_in: numText(model.waist_in),
      hips_in: numText(model.hips_in),
      shoe_size: model.shoe_size || '',
      usual_size: model.usual_size || '',
      photo_url: model.photo_url || '',
      is_active: model.is_active,
    });
    setEditing(model);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...formData,
      shoe_size: formData.shoe_size || null,
      usual_size: formData.usual_size || null,
      photo_url: formData.photo_url || null,
    };
    try {
      if (editing) {
        await adminFashionModelService.update(editing.id, payload);
      } else {
        await adminFashionModelService.create(payload);
      }
      toast.success(editing ? 'Model updated' : 'Model added');
      resetForm();
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Error saving model');
    } finally {
      setSaving(false);
    }
  };

  const sizeOptions = sizes.filter((s) => s.is_active);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">
          Models you shoot with. Pick one on a product to fill its model measurements and wearing size.
        </p>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm hover:bg-primary/90"
          >
            <Plus size={16} /> Add Model
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="border rounded-lg border-border p-4 mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input name="name" value={formData.name} onChange={handleInput} required className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Height *</label>
              <input
                name="height"
                value={formData.height}
                onChange={handleInput}
                required
                placeholder={'5\'9"'}
                className={inputClass}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              ['bust_in', 'Bust (in) *'],
              ['waist_in', 'Waist (in) *'],
              ['hips_in', 'Hips (in) *'],
            ].map(([name, label]) => (
              <div key={name}>
                <label className="block text-sm font-medium mb-1">{label}</label>
                <input
                  name={name}
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData[name]}
                  onChange={handleInput}
                  required
                  className={inputClass}
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Usual size</label>
              <select name="usual_size" value={formData.usual_size} onChange={handleInput} className={inputClass}>
                <option value="">Select size</option>
                {sizeOptions.map((s) => (
                  <option key={s.id} value={s.primary_name}>
                    {s.primary_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Shoe size</label>
              <input name="shoe_size" value={formData.shoe_size} onChange={handleInput} className={inputClass} />
            </div>
          </div>
          <ImageUploadPreview value={formData.photo_url} onChange={(url) => setField('photo_url', url)} label="Photo" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInput} />
            Active (shown in the product model dropdown)
          </label>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : editing ? 'Update Model' : 'Add Model'}
            </button>
            <button type="button" onClick={handleCancel} className="px-4 py-2 border rounded-lg border-input text-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      {models.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No models yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left border-b border-border text-muted-foreground">
                <th className="py-2">Model</th>
                <th>Height</th>
                <th>Bust / Waist / Hips (in)</th>
                <th>Usual size</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.id} className="border-b border-border/60">
                  <td className="py-2">
                    <div className="flex items-center gap-3">
                      {m.photo_url ? (
                        <img src={m.photo_url} alt={m.name} className="w-10 h-10 object-cover rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted" />
                      )}
                      {m.name}
                    </div>
                  </td>
                  <td>{m.height}</td>
                  <td>
                    {numText(m.bust_in)} / {numText(m.waist_in)} / {numText(m.hips_in)}
                  </td>
                  <td>{m.usual_size || '-'}</td>
                  <td>{m.is_active ? 'Active' : 'Inactive'}</td>
                  <td className="text-right">
                    <button
                      onClick={() => handleEdit(m)}
                      className="p-2 text-muted-foreground hover:text-foreground"
                      aria-label={`Edit ${m.name}`}
                    >
                      <Edit2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FashionModelsTab;
